import {
  attachmentsScopeLabelKey,
  formatTranslation,
  imageDisplayStyleLabelKey,
} from 'intl';
import { IntlShape } from 'react-intl';
import {
  AttachmentsScope,
  DEFAULT_INSPECTION_DATA_OPTIONS,
  DEFAULT_INSPECTION_TEMPLATE_DATA,
  DEFAULT_ISSUE_ATTACHMENTS_CONFIG,
  DEFAULT_MESSAGE_SECTION_CONFIG,
  DEFAULT_SECTION_CONFIG,
  ImageDisplayStyle,
  IssueAttachmentsConfig,
  MessageSectionConfig,
  SectionConfig,
} from '../types/model/common';
import {
  IssueAttachmentsConfigOnView,
  MessageSectionConfigOnView,
  SectionConfigOnView,
} from '../types/onView/common';
import { LabelledEntity } from 'shared/types/commonView';

export function toLabelledDisplayStyle(
  intl: IntlShape,
  displayStyle: ImageDisplayStyle
): LabelledEntity<ImageDisplayStyle> {
  return {
    _id: displayStyle,
    label: formatTranslation(intl, imageDisplayStyleLabelKey(displayStyle)),
  };
}

export function toLabelledScope(
  intl: IntlShape,
  scope: AttachmentsScope
): LabelledEntity<AttachmentsScope> {
  return {
    _id: scope,
    label: formatTranslation(intl, attachmentsScopeLabelKey(scope)),
  };
}

export function toSectionConfigOnView(
  intl: IntlShape,
  config: SectionConfig
): SectionConfigOnView {
  return {
    enabled: config.enabled,
    displayStyle: toLabelledDisplayStyle(intl, config.displayStyle),
  };
}

export function toIssueAttachmentsConfigOnView(
  intl: IntlShape,
  config: IssueAttachmentsConfig
): IssueAttachmentsConfigOnView {
  return {
    ...toSectionConfigOnView(intl, config),
    scope: toLabelledScope(intl, config.scope),
  };
}

export function toMessageSectionConfigOnView(
  intl: IntlShape,
  config: MessageSectionConfig
): MessageSectionConfigOnView {
  return {
    enabled: config.enabled,
    attachments: toSectionConfigOnView(intl, config.attachments),
  };
}

export function sectionConfigFromOnView(config: SectionConfigOnView): SectionConfig {
  return {
    enabled: config.enabled,
    displayStyle: config.displayStyle._id,
  };
}

export function issueAttachmentsConfigFromOnView(
  config: IssueAttachmentsConfigOnView
): IssueAttachmentsConfig {
  return {
    enabled: config.enabled,
    scope: config.scope._id,
    displayStyle: config.displayStyle._id,
  };
}

export function messageSectionConfigFromOnView(
  config: MessageSectionConfigOnView
): MessageSectionConfig {
  return {
    enabled: config.enabled,
    attachments: sectionConfigFromOnView(config.attachments),
  };
}

export const DEFAULT_SECTION_CONFIG_ON_VIEW = (intl: IntlShape): SectionConfigOnView =>
  toSectionConfigOnView(intl, DEFAULT_SECTION_CONFIG);

export const DEFAULT_ISSUE_ATTACHMENTS_CONFIG_ON_VIEW = (
  intl: IntlShape
): IssueAttachmentsConfigOnView => toIssueAttachmentsConfigOnView(intl, DEFAULT_ISSUE_ATTACHMENTS_CONFIG);

export const DEFAULT_MESSAGE_SECTION_CONFIG_ON_VIEW = (
  intl: IntlShape
): MessageSectionConfigOnView => toMessageSectionConfigOnView(intl, DEFAULT_MESSAGE_SECTION_CONFIG);

export type DefaultInspectionSectionConfigsOnView = {
  generalStandardAttachments: SectionConfigOnView;
  sectionAttachments: SectionConfigOnView;
  controlledElementStandardAttachments: SectionConfigOnView;
  controlledElementAttachments: SectionConfigOnView;
};

export function getDefaultInspectionSectionConfigsOnView(
  intl: IntlShape
): DefaultInspectionSectionConfigsOnView {
  return {
    generalStandardAttachments: toSectionConfigOnView(
      intl,
      DEFAULT_INSPECTION_TEMPLATE_DATA.generalStandardAttachments
    ),
    sectionAttachments: toSectionConfigOnView(
      intl,
      DEFAULT_INSPECTION_TEMPLATE_DATA.sectionAttachments
    ),
    controlledElementStandardAttachments: toSectionConfigOnView(
      intl,
      {
        enabled: true,
        displayStyle: ImageDisplayStyle.miniatures,
      }
    ),
    controlledElementAttachments: toSectionConfigOnView(
      intl,
      DEFAULT_INSPECTION_DATA_OPTIONS.controlledElementAttachments
    ),
  };
}
